import { useEffect, useState, useMemo, useRef } from "react";
import { useLessonState } from "@/hooks/useLessonState";
import { useTTS } from "@/hooks/useTTS";
import { buildSlideTtsScript } from "@/lib/script";
import { prefetchLessonTtsSlides } from "@/lib/tts";
import { Header } from "./layout/Header";
import { ProgressBar } from "./layout/ProgressBar";
import { VoiceBar } from "./layout/VoiceBar";
import { DotsNav } from "./layout/DotsNav";
import { Controls } from "./layout/Controls";
import { SlideArea } from "./SlideArea";
import { LessonCompleteFlow } from "./LessonCompleteFlow";
import { PreQuizVideo } from "./PreQuizVideo";
import { useLessonProgress } from "@/hooks/useLessonProgress";
import { usePlayerAuth } from "@/context/PlayerAuthContext";
import type { LessonAudience, LessonData } from "@/types/lesson";
import type { LessonAttachment } from "@/types/api";

export interface LessonPlayerScreenProps {
  lesson: LessonData;
  lessonId?: number;
  courseId?: number;
  ealLevel?: number;
  /** Downloadable attachments — passed from LessonView after the API fetch. */
  attachments?: LessonAttachment[];
}

export function LessonPlayerScreen({
  lesson,
  lessonId: propLessonId,
  courseId: propCourseId,
  ealLevel: propEalLevel,
  attachments = [],
}: LessonPlayerScreenProps) {
  const lessonId =
    propLessonId ?? (typeof window !== "undefined" ? window.LP_LESSON_ID ?? 0 : 0);
  const courseId =
    propCourseId ?? (typeof window !== "undefined" ? window.LP_COURSE_ID ?? 0 : 0);
  const ealLevel = propEalLevel ?? (typeof window !== "undefined" ? window.LP_EAL_LEVEL : undefined);

  const isChildTheme = useIsChildTheme(lesson.lesson_title, ealLevel, lesson.audience);
  const themeClass = isChildTheme ? "lp-theme-child" : "lp-theme-adult";
  const { isAuthenticated, isAdminPreview } = usePlayerAuth();

  // Show video intro (from lesson_content.video_url) before slide 1, per the lesson flow spec.
  const [showVideoIntro, setShowVideoIntro] = useState(!!lesson.video_url?.trim());

  const state = useLessonState(lesson, { skipCompletionAnalytics: isAdminPreview });
  const { speak, isSpeaking, stop, lastError, charIndex } = useTTS();
  /** false = tutor audio flows automatically when slide changes (natural lesson flow). */
  const [ttsPaused, setTtsPaused] = useState(false);
  const ttsPausedRef = useRef(ttsPaused);
  ttsPausedRef.current = ttsPaused;

  useEffect(() => {
    if (typeof window !== "undefined") {
      (window as unknown as { LP_LESSON_ID?: number }).LP_LESSON_ID = lessonId;
      (window as unknown as { LP_COURSE_ID?: number }).LP_COURSE_ID = courseId;
      if (ealLevel != null) (window as unknown as { LP_EAL_LEVEL?: number }).LP_EAL_LEVEL = ealLevel;
    }
  }, [lessonId, courseId, ealLevel]);

  useEffect(() => {
    const root = document.getElementById("lesson-player-root");
    if (!root) return;
    root.classList.remove("lp-theme-child", "lp-theme-adult");
    root.classList.add(isChildTheme ? "lp-theme-child" : "lp-theme-adult");
    return () => {
      root.classList.remove("lp-theme-child", "lp-theme-adult");
    };
  }, [isChildTheme]);

  const script = buildSlideTtsScript(state.currentSlide);
  const hasScript = !!script.trim();
  const yearLabel = lesson.audience?.key_stage || lesson.audience?.level_label;
  const tutorMeta = [yearLabel, "UK Tutor Voice"].filter(Boolean).join(" | ");
  const ttsContext = useMemo(
    () => ({
      isChild: lesson.audience?.is_child ?? isChildTheme,
      audienceLabel: lesson.audience?.age_group,
      yearLabel,
    }),
    [lesson.audience, isChildTheme, yearLabel]
  );

  /** Prime narration audio for the first slides as soon as the lesson loads (before first slide effect). */
  useEffect(() => {
    prefetchLessonTtsSlides(lesson.slides, 0, ttsContext);
  }, [lesson.slides, ttsContext]);

  /** Prefetch current + upcoming slides; auto-narrate when tutor isn’t paused. */
  useEffect(() => {
    stop();
    prefetchLessonTtsSlides(lesson.slides, state.index, ttsContext);
    const s = buildSlideTtsScript(state.currentSlide);
    if (!s.trim() || ttsPausedRef.current) return;
    void speak(s, ttsContext);
  }, [state.index, state.total, lesson.slides, ttsContext, speak, stop]);

  /** Stop any playing audio the moment the lesson completion screen appears. */
  useEffect(() => {
    if (state.finished) stop();
  }, [state.finished, stop]);

  useLessonProgress(courseId, lessonId, state.index, state.total, {
    enabled: isAuthenticated && !state.finished && state.total > 0 && !isAdminPreview,
  });

  const handlePlayPause = () => {
    if (isSpeaking) {
      stop();
      setTtsPaused(true);
    } else if (hasScript) {
      setTtsPaused(false);
      speak(script, ttsContext);
    }
  };

  const handleFullscreen = () => {
    const root = document.getElementById("lesson-player-root");
    if (!root) return;
    if (!document.fullscreenElement) {
      root.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  if (!lesson?.slides?.length) {
    return (
      <div className={`min-h-full flex items-center justify-center p-8 ${themeClass}`}>
        <p className="text-slate-600">No lesson data.</p>
      </div>
    );
  }

  // Step 4 of the lesson flow spec: video intro before slide 1.
  if (showVideoIntro && lesson.video_url) {
    return (
      <div className={`min-h-full flex flex-col ${themeClass}`}>
        <PreQuizVideo
          videoUrl={lesson.video_url}
          isChildTheme={isChildTheme}
          onContinue={() => setShowVideoIntro(false)}
        />
      </div>
    );
  }

  if (state.finished) {
    return (
      <div className={`min-h-full flex flex-col ${themeClass}`}>
        <LessonCompleteFlow
          lesson={lesson}
          courseId={courseId}
          lessonId={lessonId}
          isChildTheme={isChildTheme}
          onStartAgain={state.startOver}
          isAdminPreview={isAdminPreview}
          attachments={attachments}
        />
      </div>
    );
  }

  return (
    <div className={`min-h-full flex flex-col ${themeClass}`}>
      <Header
        title={lesson.lesson_title}
        meta={tutorMeta}
        current={state.index + 1}
        total={state.total}
        onFullscreen={handleFullscreen}
        isChildTheme={isChildTheme}
        adminPreview={isAdminPreview}
      />
      <ProgressBar progress={state.progress} />
      <VoiceBar
        isSpeaking={isSpeaking && !ttsPaused}
        onPlayPause={handlePlayPause}
        isPaused={ttsPaused}
        hasScript={hasScript}
        errorMessage={lastError}
        script={script}
        charIndex={charIndex}
      />
      <SlideArea
        slide={state.currentSlide}
        slideIndex={state.index}
        isChildTheme={isChildTheme}
        onQuizCorrect={() => state.setQuizDone(state.index)}
        isQuizAnswered={state.isQuizAnswered}
      />
      <DotsNav
        total={state.total}
        current={state.index}
        onSelect={state.setIndex}
        isChildTheme={isChildTheme}
      />
      <Controls
        onPrev={state.goPrev}
        onNext={state.goNext}
        isFirst={state.isFirst}
        isLast={state.isLast}
        isChildTheme={isChildTheme}
      />
    </div>
  );
}

function useIsChildTheme(lessonTitle: string, ealLevel?: number, audience?: LessonAudience): boolean {
  if (audience?.is_child === false) return false;
  if (audience?.is_child === true) return true;
  const ag = (audience?.age_group || "").toLowerCase();
  if (ag === "primary" || ag === "early_years") return true;
  if (typeof audience?.age_max === "number" && audience.age_max < 12) return true;
  if (typeof audience?.age_min === "number" && audience.age_min < 12) return true;
  if (typeof ealLevel === "number" && ealLevel >= 6) return false;
  const title = (lessonTitle || "").toLowerCase();
  return /ks1|ks2|years?\s*[1-6]|year\s*[1-6]|early_years|primary/i.test(title);
}
